import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import { DELIVERY_LOCATIONS } from "../data/deliveryLocations";
import {
  createAddressFromLocation,
  saveSelectedDeliveryLocation,
  SHIPPING_CHARGE,
} from "../utils/deliveryService";

export default function DeliveryLocationSelector() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [selectedLocation, setSelectedLocation] = useState(null);

  const filteredLocations = useMemo(() => {
    const q = query.trim().toLowerCase();

    if (!q) return DELIVERY_LOCATIONS;

    return DELIVERY_LOCATIONS.filter((item) =>
      [
        item.area,
        item.city,
        item.district,
        item.state,
        item.pincode,
        item.region,
      ]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [query]);

  const groupedLocations = useMemo(() => {
    return filteredLocations.reduce((acc, item) => {
      if (!acc[item.region]) acc[item.region] = [];
      acc[item.region].push(item);
      return acc;
    }, {});
  }, [filteredLocations]);

  const handleContinue = () => {
    if (!selectedLocation) return;

    const addressPayload = createAddressFromLocation(selectedLocation);
    saveSelectedDeliveryLocation(addressPayload);

    navigate("/checkout", {
      state: {
        selectedDeliveryLocation: addressPayload,
      },
    });
  };

  return (
    <>
      <Navbar />
      <main className="delivery-selector-page pt-100 pb-100">
        <div className="container">
          <div className="selector-hero">
            <div className="hero-badge">Delivery Availability</div>
            <h1>Select your delivery location</h1>
            <p>
              We currently deliver only to supported Madurai and Peraiyur
              locations. Choose your area to continue with checkout.
            </p>
          </div>

          <div className="selector-layout">
            <section className="selector-panel">
              <div className="search-wrap">
                <i className="fa-solid fa-magnifying-glass search-icon"></i>
                <input
                  type="text"
                  className="selector-search"
                  placeholder="Search by area, city, district or pincode"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>

              <div className="results-meta">
                {filteredLocations.length} serviceable locations found
              </div>

              {Object.keys(groupedLocations).length === 0 ? (
                <div className="empty-box">No matching location found.</div>
              ) : (
                Object.entries(groupedLocations).map(([region, items]) => (
                  <div key={region} className="location-group">
                    <div className="group-title">{region}</div>

                    <div className="group-grid">
                      {items.map((location) => {
                        const active = selectedLocation?.id === location.id;

                        return (
                          <button
                            type="button"
                            key={location.id}
                            className={`location-card ${active ? "active" : ""}`}
                            onClick={() => setSelectedLocation(location)}
                          >
                            <div className="card-top">
                              <div>
                                <h4>{location.area}</h4>
                                <p>
                                  {location.city}, {location.district}
                                </p>
                              </div>
                              {active && (
                                <i className="fa-solid fa-circle-check active-icon"></i>
                              )}
                            </div>

                            <div className="card-bottom">
                              <span className="pin-badge">
                                {location.pincode}
                              </span>
                              <span className="state-text">
                                {location.state}
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </section>

            <aside className="selection-summary">
              <div className="summary-card">
                <h3>Selected location</h3>

                {selectedLocation ? (
                  <>
                    <div className="summary-address">
                      <h4>{selectedLocation.area}</h4>
                      <p>
                        {selectedLocation.city}, {selectedLocation.district}
                      </p>
                      <p>
                        {selectedLocation.state} - {selectedLocation.pincode}
                      </p>
                      <span className="delivery-status success">
                        Delivery available
                      </span>
                    </div>

                    <div className="summary-note">
                      Shipping charge: <strong>Rs. {SHIPPING_CHARGE}</strong>
                    </div>

                    <button
                      type="button"
                      className="continue-btn"
                      onClick={handleContinue}
                    >
                      Continue to checkout
                    </button>
                  </>
                ) : (
                  <div className="summary-placeholder">
                    Select one supported location from the list.
                  </div>
                )}
              </div>
            </aside>
          </div>
        </div>
      </main>
      <Footer />

      <style jsx>{`
        .delivery-selector-page {
          background: #f6f7fb;
          min-height: 100vh;
        }
        .selector-hero {
          max-width: 760px;
          margin: 0 auto 32px;
          text-align: center;
        }
        .hero-badge {
          display: inline-block;
          padding: 8px 14px;
          border-radius: 999px;
          background: #eef4ff;
          color: #175cd3;
          font-size: 13px;
          font-weight: 700;
          margin-bottom: 14px;
        }
        .selector-hero h1 {
          font-size: 38px;
          font-weight: 800;
          color: #101828;
          margin-bottom: 12px;
        }
        .selector-hero p {
          font-size: 16px;
          color: #667085;
          margin: 0 auto;
          max-width: 640px;
        }
        .selector-layout {
          display: grid;
          grid-template-columns: 1.4fr 0.7fr;
          gap: 28px;
          align-items: start;
        }
        .selector-panel,
        .summary-card {
          background: #fff;
          border-radius: 20px;
          border: 1px solid #e9edf5;
          box-shadow: 0 10px 35px rgba(16, 24, 40, 0.06);
        }
        .selector-panel {
          padding: 24px;
        }
        .search-wrap {
          position: relative;
          margin-bottom: 16px;
        }
        .selector-search {
          width: 100%;
          height: 56px;
          border-radius: 14px;
          border: 1px solid #d0d5dd;
          padding: 0 16px 0 46px;
          font-size: 15px;
          outline: none;
        }
        .selector-search:focus {
          border-color: #175cd3;
          box-shadow: 0 0 0 4px rgba(23, 92, 211, 0.1);
        }
        .search-icon {
          position: absolute;
          top: 50%;
          left: 16px;
          transform: translateY(-50%);
          color: #667085;
        }
        .results-meta {
          font-size: 14px;
          color: #667085;
          margin-bottom: 18px;
        }
        .location-group + .location-group {
          margin-top: 28px;
        }
        .group-title {
          font-size: 18px;
          font-weight: 800;
          color: #111827;
          margin-bottom: 14px;
        }
        .group-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 14px;
        }
        .location-card {
          border: 1px solid #e4e7ec;
          border-radius: 16px;
          background: #fff;
          padding: 16px;
          text-align: left;
          transition: all 0.2s ease;
        }
        .location-card:hover {
          border-color: #175cd3;
          box-shadow: 0 8px 24px rgba(23, 92, 211, 0.08);
        }
        .location-card.active {
          border-color: #175cd3;
          background: #f5f8ff;
          box-shadow: 0 0 0 3px rgba(23, 92, 211, 0.08);
        }
        .card-top {
          display: flex;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 12px;
        }
        .card-top h4 {
          font-size: 16px;
          font-weight: 700;
          color: #101828;
          margin: 0 0 4px;
        }
        .card-top p {
          margin: 0;
          font-size: 14px;
          color: #667085;
        }
        .active-icon {
          color: #175cd3;
          font-size: 18px;
        }
        .card-bottom {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 10px;
        }
        .pin-badge {
          background: #eef4ff;
          color: #175cd3;
          border-radius: 999px;
          padding: 6px 10px;
          font-size: 12px;
          font-weight: 700;
        }
        .state-text {
          font-size: 13px;
          color: #667085;
        }
        .summary-card {
          padding: 24px;
          position: sticky;
          top: 110px;
        }
        .summary-card h3 {
          font-size: 22px;
          font-weight: 800;
          margin-bottom: 18px;
          color: #111827;
        }
        .summary-address h4 {
          font-size: 18px;
          font-weight: 700;
          margin-bottom: 8px;
          color: #101828;
        }
        .summary-address p {
          margin: 0 0 6px;
          color: #667085;
          font-size: 14px;
        }
        .delivery-status.success {
          display: inline-block;
          margin-top: 10px;
          padding: 8px 12px;
          border-radius: 999px;
          background: #ecfdf3;
          border: 1px solid #abefc6;
          color: #027a48;
          font-size: 12px;
          font-weight: 700;
        }
        .summary-note {
          margin: 18px 0;
          padding: 14px 16px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 14px;
          font-size: 15px;
          color: #334155;
        }
        .continue-btn {
          width: 100%;
          min-height: 52px;
          border: 0;
          border-radius: 12px;
          background: #175cd3;
          color: #fff;
          font-size: 16px;
          font-weight: 700;
        }
        .summary-placeholder,
        .empty-box {
          padding: 18px;
          border-radius: 14px;
          background: #f8fafc;
          color: #64748b;
          font-size: 14px;
          border: 1px dashed #cbd5e1;
        }
        @media (max-width: 991px) {
          .selector-layout {
            grid-template-columns: 1fr;
          }
          .summary-card {
            position: static;
          }
        }
        @media (max-width: 767px) {
          .group-grid {
            grid-template-columns: 1fr;
          }
          .selector-hero h1 {
            font-size: 30px;
          }
        }
      `}</style>
    </>
  );
}
